import React, { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Helmet } from 'react-helmet-async';

const formatPercent = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  return `${(Number(value) * 100).toFixed(digits)}%`;
};

const formatSignedPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  const percentage = Number(value) * 100;
  return `${percentage > 0 ? '+' : ''}${percentage.toFixed(1)} pp`;
};

const formatOdds = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return '-';
  }
  return Number(value).toFixed(2);
};

const differenceColor = (value) => {
  if (value === null || value === undefined) return 'text.secondary';
  if (value > 0) return '#4ade80';
  if (value < 0) return '#fb7185';
  return 'text.primary';
};

const eventKey = (row) => `${row.teamId}|${row.eventType}|${row.groupIdentifier || ''}`;

const rowWithOdd = (row, odd) => {
  const impliedProbability = 1 / Number(odd.effectiveOdds);
  const difference = Number(row.symuligaProbability) - impliedProbability;
  const value = Number(row.symuligaProbability) * Number(odd.effectiveOdds);
  return {
    ...row,
    bookmaker: odd.bookmaker,
    rawOdds: odd.rawOdds,
    effectiveOdds: odd.effectiveOdds,
    taxMultiplier: odd.taxMultiplier,
    impliedProbability,
    difference,
    absoluteDifference: Math.abs(difference),
    value
  };
};

const sortableColumns = [
  { id: 'symuligaProbability', label: 'SymuLiga' },
  { id: 'impliedProbability', label: 'Buk' },
  { id: 'difference', label: 'Roznica' },
  { id: 'rawOdds', label: 'Kurs' },
  { id: 'effectiveOdds', label: 'Kurs netto' },
  { id: 'value', label: 'Value' }
];

const WcBookmakerOddsComparison = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orderBy, setOrderBy] = useState('value');
  const [order, setOrder] = useState('desc');
  const [minValue, setMinValue] = useState('1.05');
  const [minDifference, setMinDifference] = useState('0.3');
  const [selectedBookmakers, setSelectedBookmakers] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/simulation/worldcup/bookmaker-odds-comparison');
      setData(response.data);
    } catch (err) {
      setError(err.response?.data || err.message || 'Nie udalo sie pobrac danych.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const bookmakers = useMemo(() => {
    return [...new Set((data?.rows || []).map((row) => row.bookmaker))].sort();
  }, [data]);

  useEffect(() => {
    if (bookmakers.length > 0 && selectedBookmakers.length === 0) {
      setSelectedBookmakers(bookmakers);
    }
  }, [bookmakers, selectedBookmakers.length]);

  const rows = useMemo(() => {
    const minValueNumber = minValue === '' ? Number.NEGATIVE_INFINITY : Number(minValue);
    const minDifferenceNumber = minDifference === '' ? Number.NEGATIVE_INFINITY : Number(minDifference) / 100;

    const byEvent = new Map();
    (data?.rows || []).forEach((row) => {
      if (!byEvent.has(eventKey(row))) {
        byEvent.set(eventKey(row), row);
      }
    });

    return [...byEvent.values()].map((row) => {
      const visibleOdds = (row.marketOdds || [])
        .filter((odd) => selectedBookmakers.length === 0 || selectedBookmakers.includes(odd.bookmaker))
        .sort((a, b) => Number(b.effectiveOdds) - Number(a.effectiveOdds));
      if (visibleOdds.length === 0) {
        return null;
      }
      return rowWithOdd({ ...row, marketOdds: visibleOdds }, visibleOdds[0]);
    }).filter((row) => {
      if (!row) {
        return false;
      }
      const valueMatches = Number(row.value) >= minValueNumber;
      const differenceMatches = Math.abs(Number(row.difference)) >= minDifferenceNumber;
      return valueMatches && differenceMatches;
    }).sort((a, b) => {
      const aValue = orderBy === 'difference' ? Math.abs(Number(a.difference)) : a[orderBy];
      const bValue = orderBy === 'difference' ? Math.abs(Number(b.difference)) : b[orderBy];
      const normalizedA = aValue === null || aValue === undefined ? Number.NEGATIVE_INFINITY : Number(aValue);
      const normalizedB = bValue === null || bValue === undefined ? Number.NEGATIVE_INFINITY : Number(bValue);
      return order === 'asc' ? normalizedA - normalizedB : normalizedB - normalizedA;
    });
  }, [data, minDifference, minValue, order, orderBy, selectedBookmakers]);

  const handleSort = (columnId) => {
    const isSameColumn = orderBy === columnId;
    setOrder(isSameColumn && order === 'desc' ? 'asc' : 'desc');
    setOrderBy(columnId);
  };

  const renderSortLabel = (column) => (
    <TableSortLabel
      active={orderBy === column.id}
      direction={orderBy === column.id ? order : 'desc'}
      onClick={() => handleSort(column.id)}
    >
      {column.label}
    </TableSortLabel>
  );

  const toggleBookmaker = (bookmaker) => {
    setSelectedBookmakers((current) => (
      current.includes(bookmaker)
        ? current.filter((item) => item !== bookmaker)
        : [...current, bookmaker]
    ));
  };

  const renderOddsTooltip = (row) => (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" sx={{ display: 'block', mb: 0.5, fontWeight: 700 }}>
        Kursy dla tego zdarzenia
      </Typography>
      {(row.marketOdds || []).map((odd) => (
        <Stack key={`${odd.bookmaker}-${odd.rawOdds}-${odd.effectiveOdds}`} direction="row" spacing={1.5} justifyContent="space-between">
          <Typography variant="caption">{odd.bookmaker}</Typography>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {formatOdds(odd.effectiveOdds)} netto ({formatOdds(odd.rawOdds)})
          </Typography>
        </Stack>
      ))}
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2, md: 3 }, py: 3 }}>
      <Helmet>
        <title>SymuLiga vs bukmacherzy | symuliga.pl</title>
      </Helmet>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
            SymuLiga vs bukmacherzy
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Kursy bukmacherow poza Betclic sa liczone po podatku 12%, czyli kurs netto = kurs x 0.88.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>
          Odswiez
        </Button>
      </Stack>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {String(error)}
        </Alert>
      )}

      {!loading && !error && data && (
        <>
          {Array.isArray(data.bookmakerWarnings) && data.bookmakerWarnings.length > 0 && (
            <Alert severity={data.bookmakersAvailable ? 'warning' : 'error'} sx={{ mb: 3 }}>
              {data.bookmakerWarnings.join(' | ')}
            </Alert>
          )}

          <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="Min. Value"
                  type="number"
                  size="small"
                  value={minValue}
                  onChange={(event) => setMinValue(event.target.value)}
                  inputProps={{ step: '0.01', min: '0' }}
                  sx={{ width: { xs: '100%', sm: 150 } }}
                />
                <TextField
                  label="Min. roznica (pp)"
                  type="number"
                  size="small"
                  value={minDifference}
                  onChange={(event) => setMinDifference(event.target.value)}
                  inputProps={{ step: '0.1', min: '0' }}
                  sx={{ width: { xs: '100%', sm: 170 } }}
                />
              </Stack>
              <FormGroup row sx={{ gap: { xs: 0, md: 1 } }}>
                {bookmakers.map((bookmaker) => (
                  <FormControlLabel
                    key={bookmaker}
                    control={(
                      <Checkbox
                        checked={selectedBookmakers.includes(bookmaker)}
                        onChange={() => toggleBookmaker(bookmaker)}
                        size="small"
                      />
                    )}
                    label={bookmaker}
                  />
                ))}
              </FormGroup>
            </Stack>
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
            <TableContainer component={Paper} sx={{ borderRadius: 2, width: 'auto', maxWidth: '100%', display: 'inline-block' }}>
              <Table size="small" stickyHeader sx={{ width: 'auto', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ pl: 3, whiteSpace: 'nowrap' }}>Druzyna</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Zdarzenie</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Najlepszy buk</TableCell>
                    {sortableColumns.map((column, index) => (
                      <TableCell
                        key={column.id}
                        align="right"
                        sx={{ pr: index === sortableColumns.length - 1 ? 3 : undefined, whiteSpace: 'nowrap' }}
                      >
                        {renderSortLabel(column)}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <Tooltip
                      key={`${row.teamId}-${row.eventType}-${row.bookmaker}-${row.rawOdds}`}
                      title={renderOddsTooltip(row)}
                      arrow
                      placement="top"
                    >
                      <TableRow hover>
                        <TableCell sx={{ pl: 3, whiteSpace: 'nowrap' }}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.teamName}
                            </Typography>
                            {row.groupIdentifier && (
                              <Typography variant="caption" color="text.secondary">
                                Grupa {row.groupIdentifier}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.eventLabel}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.bookmaker}</TableCell>
                        <TableCell align="right">{formatPercent(row.symuligaProbability)}</TableCell>
                        <TableCell align="right">{formatPercent(row.impliedProbability)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ color: differenceColor(row.difference), fontWeight: 700 }}>
                            {formatSignedPercent(row.difference)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{formatOdds(row.rawOdds)}</TableCell>
                        <TableCell align="right">{formatOdds(row.effectiveOdds)}</TableCell>
                        <TableCell align="right" sx={{ pr: 3, fontWeight: 700 }}>{formatOdds(row.value)}</TableCell>
                      </TableRow>
                    </Tooltip>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 5, px: 3 }}>
                        <Typography color="text.secondary">
                          Brak aktualnych okazji value powyzej 1.00.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
};

export default WcBookmakerOddsComparison;
